// apps/web/src/features/commercial-proposals/components/CommercialProposalsListPage.tsx
//
// Page entry for `/kp` (#1198). Operational workspace: heading, a
// manage-gated create button, a desktop grid table, and a mobile card list
// (`useNarrow`). Managers see only their own rows (the backend scopes the
// list by ownership); an Admin sees all rows. The `<Outlet />` mounts the
// create editor (`/kp/new`) and the detail drawer (`/kp/:id`) as sibling
// overlays so the list cache survives navigation.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router';
import type { CommercialProposalListItem } from '@magimp/shared';

import { Button, ToolbarActions, useNarrow } from '../../../ui/index.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import type { AppLocale } from '../../../i18n/index.js';
import { useCommercialProposalsList } from '../queries.js';
import { CommercialProposalsListTable } from './CommercialProposalsListTable.js';
import { CommercialProposalMobileCard } from './CommercialProposalMobileCard.js';

export function CommercialProposalsListPage() {
  const { t, i18n } = useTranslation('commercialProposals');
  const navigate = useNavigate();
  const narrow = useNarrow();
  const permissions = useAuthStore((s) => s.permissions);
  const canManage = hasPermission(permissions, 'commercialProposals.manage');
  // The owner column is Admin-only: a manager already sees only its own rows,
  // so an owner label would be redundant and out of scope for that role.
  const isAdmin = permissions === 'ALL';
  const language = i18n.language as AppLocale;

  const query = useCommercialProposalsList();
  const rows = useMemo<readonly CommercialProposalListItem[]>(
    () => query.data?.items ?? [],
    [query.data?.items],
  );

  const openRow = (id: string) => navigate(`/kp/${encodeURIComponent(id)}`);

  return (
    <div
      data-testid="commercial-proposals-list-page"
      style={{
        position: 'relative',
        margin: -16,
        height: 'calc(100% + 32px)',
        width: 'calc(100% + 32px)',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-0)',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '18px 24px 12px',
          display: 'flex',
          alignItems: 'flex-end',
          gap: 16,
          borderBottom: '1px solid var(--border-1)',
          flex: 'none',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            color: 'var(--fg-1)',
            letterSpacing: '-.005em',
          }}
        >
          {t('title')}
        </h1>
        <div style={{ flex: 1 }} />
        <ToolbarActions>
          {canManage && (
            <Button
              kind="primary"
              icon="plus"
              data-testid="commercial-proposals-new-link"
              onClick={() => navigate('/kp/new')}
            >
              {t('actions.create')}
            </Button>
          )}
        </ToolbarActions>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: 'var(--bg-0)' }}>
        {query.isLoading ? (
          <p
            data-testid="commercial-proposals-loading"
            style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}
          >
            {t('loading')}
          </p>
        ) : query.isError ? (
          <p
            data-testid="commercial-proposals-error"
            role="alert"
            style={{ padding: 24, color: 'var(--status-error-fg)', fontSize: 13 }}
          >
            {t('error')}
          </p>
        ) : rows.length === 0 ? (
          <p
            data-testid="commercial-proposals-empty"
            style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}
          >
            {t('empty')}
          </p>
        ) : narrow ? (
          <ul
            data-testid="commercial-proposals-cards"
            style={{
              listStyle: 'none',
              margin: 0,
              padding: '12px 12px 0',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {rows.map((row) => (
              <li key={row.id} style={{ listStyle: 'none' }}>
                <CommercialProposalMobileCard
                  row={row}
                  language={language}
                  showOwner={isAdmin}
                  onSelect={() => openRow(row.id)}
                />
              </li>
            ))}
          </ul>
        ) : (
          <CommercialProposalsListTable
            rows={rows}
            language={language}
            showOwner={isAdmin}
            onOpenRow={openRow}
          />
        )}
      </div>

      {/* `Outlet` mounts `CommercialProposalEditor` on `/kp/new` and
          `CommercialProposalDrawer` on `/kp/:id`, overlaying the list. */}
      <Outlet />
    </div>
  );
}
